/**
 * Project lifecycle classification — one place decides what "gone" means.
 *
 * The problem this exists to remove: the server answers **404** for a deleted
 * project, a project the caller was removed from, AND a project that never
 * existed — deliberately, so membership cannot be probed (see the access matrix
 * in scripts/ai/project-handlers.js). The client used to read that entire class
 * as "no cloud copy yet" and keep editing local state, so a deleted project
 * loaded from a stale tab with no indication at all.
 *
 * A status code therefore CANNOT be classified on its own. Two extra inputs are
 * required, and both are load-bearing:
 *
 *  - `operation` — a 404 from a read is ambiguous; a 409-with-null-project from
 *    a save is not (it is only reachable with baseVersion > 0, i.e. previously
 *    synced), and a WebSocket close code states the cause outright.
 *  - `everConfirmedInCloud` — a one-way latch, set the first time the server
 *    confirms this project exists for this principal. Without it, a brand-new
 *    project that has never synced (`cloudVersion === null`, which migrateState
 *    assigns to every new project) would have its perfectly normal 404 read as
 *    "deleted", and the builder would refuse to edit a project the user just
 *    created offline. That regression is the reason this parameter exists.
 *
 * Pure: no imports, no I/O, no DOM. Same discipline as
 * shared/export-gate-policy.js — the consumers (save-load, collab-client,
 * shell) read their behavior from here so the gate cannot be described one way
 * and behave another.
 */

/**
 * @typedef {'local-only'|'checking'|'live'|'unavailable'|'indeterminate'} LifecycleState
 */

export const LIFECYCLE = Object.freeze({
  /** Never confirmed in cloud. A 404 here is expected and means nothing is wrong. */
  LOCAL_ONLY: 'local-only',
  /** A confirmation is in flight. Boot's default. */
  CHECKING: 'checking',
  /** The server confirmed this project exists and we have access. */
  LIVE: 'live',
  /** Authoritatively gone or inaccessible. The only state that blocks writing. */
  UNAVAILABLE: 'unavailable',
  /** Could not reach the server. Keep local, retry, say nothing alarming. */
  INDETERMINATE: 'indeterminate'
});

/** Operations whose failures carry lifecycle meaning. */
export const LIFECYCLE_OPERATION = Object.freeze({
  /** GET project metadata / version bundle — 404 is AMBIGUOUS by design. */
  READ: 'read',
  /** POST a new version — 409/403 here are unambiguous. */
  SAVE: 'save',
  /** The collab WebSocket closing — the server states the cause in the code. */
  SOCKET: 'socket'
});

/**
 * Close codes the room uses to state WHY it hung up. Mirrors CLOSE_CODES in
 * shared/collab-protocol.js; duplicated as plain numbers rather than imported so
 * this module stays import-free and Worker/headless-safe. The parity is pinned
 * by project-lifecycle-tests.js, which imports both and asserts they agree — so
 * this copy cannot silently drift from the protocol.
 */
export const SOCKET_LIFECYCLE_CLOSE_CODES = Object.freeze({
  MEMBERSHIP_REVOKED: 4403,
  PROJECT_DELETED: 4404
});

/**
 * The signal a tab that PERFORMED a delete announces to its siblings. It knows
 * the cause first-hand, so the receiver gets a stated cause rather than having
 * to re-derive one from an ambiguous 404. Same value the 4404 close produces.
 */
export const LIFECYCLE_SIGNAL_DELETED = 'socket-deleted';

/** True for a lifecycle state in which the project must not be written to. */
export function blocksWriting(lifecycle) {
  return lifecycle === LIFECYCLE.UNAVAILABLE;
}

/**
 * True when the cause of unavailability is known well enough to say "deleted"
 * to a user. A read-path 404 proves the project is gone FOR US but cannot
 * distinguish deletion from access loss, so its copy must stay cause-neutral.
 */
export function hasStatedCause(signal) {
  return signal === 'socket-deleted' || signal === 'socket-revoked' || signal === 'save-conflict';
}

/**
 * Classify one server signal into a lifecycle state.
 *
 * @param {object} input
 * @param {'read'|'save'|'socket'} input.operation
 * @param {number|null} [input.status] - HTTP status (read/save). 0 or null = transport failure.
 * @param {number|null} [input.closeCode] - WebSocket close code (socket).
 * @param {object|null} [input.payload] - parsed response body, when there is one.
 * @param {boolean} [input.everConfirmedInCloud] - has the server EVER confirmed
 *   this project for this principal? Defaults false, which is the safe default:
 *   an unconfirmed project is treated as local-only rather than unavailable.
 * @returns {{lifecycle: LifecycleState, signal: string}} `signal` names the
 *   evidence, so callers can pick honest copy (see hasStatedCause).
 */
let lifecycleObserver = null;

export function configureProjectLifecycleObserver(observer) {
  lifecycleObserver = typeof observer === 'function' ? observer : null;
}

function classifyProjectSignalRaw({
  operation,
  status = null,
  closeCode = null,
  payload = null,
  everConfirmedInCloud = false
} = {}) {
  if (operation === LIFECYCLE_OPERATION.SOCKET) {
    if (closeCode === SOCKET_LIFECYCLE_CLOSE_CODES.PROJECT_DELETED) {
      return { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'socket-deleted' };
    }
    if (closeCode === SOCKET_LIFECYCLE_CLOSE_CODES.MEMBERSHIP_REVOKED) {
      return { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'socket-revoked' };
    }
    // Every other close is a transport event, not a lifecycle event. Reconnect.
    return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'socket-transport' };
  }

  // No status at all means the request never reached a server that could answer.
  // Conflating this with absence is the original bug — an offline user must not
  // be told their project was deleted.
  if (!status) return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'transport' };

  if (operation === LIFECYCLE_OPERATION.SAVE) {
    // 409 carrying `project: null` — the server had no live row to describe.
    // Only reachable when the client sent baseVersion > 0, so the project was
    // definitely synced once and is definitely gone now.
    if (status === 409 && payload && payload.project == null) {
      return { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'save-conflict' };
    }
    // 409 WITH a project is an ordinary concurrent-edit conflict, not lifecycle.
    if (status === 409) return { lifecycle: LIFECYCLE.LIVE, signal: 'version-conflict' };
    if (status === 403) return { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'save-forbidden' };
    if (status === 404) {
      return everConfirmedInCloud
        ? { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'save-missing' }
        : { lifecycle: LIFECYCLE.LOCAL_ONLY, signal: 'never-synced' };
    }
    if (status === 401) return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'unauthenticated' };
    return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'save-error' };
  }

  // READ. 404 is the ambiguous one and is the whole reason this module exists.
  if (status === 404) {
    return everConfirmedInCloud
      ? { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'read-missing' }
      : { lifecycle: LIFECYCLE.LOCAL_ONLY, signal: 'never-synced' };
  }
  // 401 is an auth problem, never a project problem: signing back in restores
  // access, so marking the project unavailable would be both wrong and sticky.
  if (status === 401) return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'unauthenticated' };
  if (status === 403) return { lifecycle: LIFECYCLE.UNAVAILABLE, signal: 'read-forbidden' };
  if (status >= 200 && status < 300) return { lifecycle: LIFECYCLE.LIVE, signal: 'confirmed' };
  // 5xx, 429, anything else the server said while alive: transient by default.
  return { lifecycle: LIFECYCLE.INDETERMINATE, signal: 'read-error' };
}

export function classifyProjectSignal(input = {}) {
  const result = classifyProjectSignalRaw(input);
  try {
    lifecycleObserver?.({
      operation: input.operation || 'read',
      lifecycle: result.lifecycle,
      signal: result.signal
    });
  } catch {
    // Observability can never change lifecycle classification.
  }
  return result;
}

/**
 * User-facing copy for an unavailable project. Cause-neutral unless the signal
 * actually stated a cause — a read-path 404 cannot tell deletion from access
 * loss, and claiming "deleted" there would be a guess presented as a fact.
 * @param {string} signal
 * @returns {string}
 */
export function unavailableMessageFor(signal) {
  if (signal === 'socket-deleted') return 'This project was deleted.';
  if (signal === 'socket-revoked') return 'You no longer have access to this project.';
  if (signal === 'save-conflict' || signal === 'save-missing') {
    return 'This project no longer exists on the server.';
  }
  if (signal === 'save-forbidden' || signal === 'read-forbidden') {
    return 'You no longer have access to this project.';
  }
  return 'This project is no longer available on the server.';
}
